import { useCallback, useMemo } from "react";
import { navigateTo } from "../../utils/navigation";
import styles from "./ReturnToAppButton.module.css";

type Platform = "android" | "ios" | "web";

const ANDROID_RETURN_URL = process.env.REACT_APP_ANDROID_RETURN_URL ?? "petsrwise://payment-success";
const IOS_RETURN_URL = process.env.REACT_APP_IOS_RETURN_URL ?? "petsrwise://payment-success";
const WEB_RETURN_URL = process.env.REACT_APP_WEB_RETURN_URL ?? "petsrwise://payment-success";
const RETURN_TO_APP_MESSAGE_TYPE = "RETURN_TO_APP" as const;
const NATIVE_HANDOFF_DELAY_MS = 400;
const NAVIGATION_FALLBACK_DELAY_MS = 1500;

const appendBookingCode = (baseUrl: string, bookingCode?: string): string => {
  if (!bookingCode) {
    return baseUrl;
  }

  try {
    const url = new URL(baseUrl);
    url.searchParams.set("bookingCode", bookingCode);
    return url.toString();
  } catch {
    const separator = baseUrl.includes("?") ? "&" : "?";
    return `${baseUrl}${separator}bookingCode=${encodeURIComponent(bookingCode)}`;
  }
};

const detectPlatform = (): Platform => {
  if (typeof navigator === "undefined") {
    return "web";
  }

  const userAgent = navigator.userAgent || navigator.vendor || "";

  if (/android/i.test(userAgent)) {
    return "android";
  }

  if (/iPad|iPhone|iPod/i.test(userAgent)) {
    return "ios";
  }

  return "web";
};

interface ReturnToAppButtonProps {
  label: string;
  className?: string;
  bookingCode?: string;
  orderCode?: string;
}

interface ReturnToAppMessage {
  type: typeof RETURN_TO_APP_MESSAGE_TYPE;
  returnUrl: string;
}

const notifyNativeApp = (message: ReturnToAppMessage): boolean => {
  if (typeof window === "undefined") {
    return false;
  }

  const payload = JSON.stringify(message);
  let handled = false;

  const reactNativeBridge = window.ReactNativeWebView?.postMessage;
  if (typeof reactNativeBridge === "function") {
    reactNativeBridge(payload);
    handled = true;
  }

  const webkitBridge = window.webkit?.messageHandlers?.returnToApp;
  if (typeof webkitBridge?.postMessage === "function") {
    webkitBridge.postMessage(payload);
    handled = true;
  }

  return handled;
};

const navigateWithFallback = (platform: Platform, targetUrl: string): void => {
  if (typeof window === "undefined") {
    return;
  }

  navigateTo(targetUrl);

  if (platform === "web" || typeof document === "undefined") {
    return;
  }

  const timeoutId = window.setTimeout(() => {
    navigateTo(WEB_RETURN_URL);
  }, NAVIGATION_FALLBACK_DELAY_MS);

  const handleVisibilityChange = () => {
    if (document.hidden) {
      window.clearTimeout(timeoutId);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    }
  };

  document.addEventListener("visibilitychange", handleVisibilityChange);
};

const scheduleNativeFallback = (platform: Platform, targetUrl: string): void => {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return;
  }

  const timeoutId = window.setTimeout(() => {
    navigateWithFallback(platform, targetUrl);
  }, NATIVE_HANDOFF_DELAY_MS);

  const handleVisibilityChange = () => {
    if (document.hidden) {
      window.clearTimeout(timeoutId);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    }
  };

  document.addEventListener("visibilitychange", handleVisibilityChange);
};

export const ReturnToAppButton = ({ label, className, bookingCode, orderCode }: ReturnToAppButtonProps) => {
  const platform = useMemo(() => detectPlatform(), []);
  const effectiveBookingCode = bookingCode ?? orderCode;
  const androidUrl = useMemo(() => appendBookingCode(ANDROID_RETURN_URL, effectiveBookingCode), [effectiveBookingCode]);
  const iosUrl = useMemo(() => appendBookingCode(IOS_RETURN_URL, effectiveBookingCode), [effectiveBookingCode]);

  const handleClick = useCallback(() => {
    if (typeof window === "undefined") {
      return;
    }

    const targetUrl = platform === "android" ? androidUrl : platform === "ios" ? iosUrl : WEB_RETURN_URL;
    const message: ReturnToAppMessage = { type: RETURN_TO_APP_MESSAGE_TYPE, returnUrl: targetUrl };

    if (platform !== "web" && notifyNativeApp(message)) {
      scheduleNativeFallback(platform, targetUrl);
      return;
    }

    navigateWithFallback(platform, targetUrl);
  }, [androidUrl, iosUrl, platform]);

  const classNames = className ? `${styles.button} ${className}` : styles.button;

  return (
    <button
      type="button"
      className={classNames}
      onClick={handleClick}
      data-android-href={androidUrl}
      data-ios-href={iosUrl}
    >
      {label}
    </button>
  );
};
